export const DISTRIBUTION_BOARD_ASSET_TYPES = new Set([
  "main_distributor",
  "sub_distributor",
  "distribution_board",
  "main_distribution_board",
  "sub_distribution_board",
]);

export function isDistributionBoardAssetType(value) {
  return DISTRIBUTION_BOARD_ASSET_TYPES.has(String(value || "").trim());
}

export function normalizeGeoGeometry(input, options = {}) {
  const padding = Math.max(Number(options.padding ?? 40), 0);
  const targetWidth = Math.max(Number(options.targetWidth ?? 720), 1);
  const targetHeight = Math.max(Number(options.targetHeight ?? 520), 1);
  const collection = toFeatureCollection(input);

  if (!collection.features.length) {
    return {
      geometry: null,
      features: [],
      transform: {
        originX: 0,
        originY: 0,
        scale: 1,
        rotation: 0,
        boundingBox: [0, 0, 0, 0],
      },
      width: targetWidth,
      height: targetHeight,
    };
  }

  const pointBounds = getCollectionBounds(collection.features);
  const availableWidth = Math.max(targetWidth - padding * 2, 1);
  const availableHeight = Math.max(targetHeight - padding * 2, 1);
  const sourceWidth = Math.max(pointBounds.maxX - pointBounds.minX, 1);
  const sourceHeight = Math.max(pointBounds.maxY - pointBounds.minY, 1);
  const scale = Math.min(availableWidth / sourceWidth, availableHeight / sourceHeight);
  const offsetX = padding + (availableWidth - sourceWidth * scale) / 2;
  const offsetY = padding + (availableHeight - sourceHeight * scale) / 2;

  const features = collection.features.map((feature) => {
    const normalizedGeometry = mapGeometryPoints(feature.geometry, ([x, y]) => [
      offsetX + (x - pointBounds.minX) * scale,
      targetHeight - (offsetY + (y - pointBounds.minY) * scale),
    ]);
    const bounds = getGeometryBounds(normalizedGeometry);
    return {
      ...feature,
      geometry: normalizedGeometry,
      bounds,
    };
  });

  return {
    geometry: {
      type: "FeatureCollection",
      features: features.map((feature) => ({
        type: "Feature",
        properties: feature.properties || {},
        geometry: feature.geometry,
      })),
    },
    features,
    transform: {
      originX: pointBounds.minX,
      originY: pointBounds.minY,
      scale,
      rotation: 0,
      boundingBox: [
        pointBounds.minX,
        pointBounds.minY,
        pointBounds.maxX,
        pointBounds.maxY,
      ],
    },
    width: targetWidth,
    height: targetHeight,
  };
}

export function toFeatureCollection(input) {
  if (!input || typeof input !== "object") {
    return { type: "FeatureCollection", features: [] };
  }

  if (input.type === "FeatureCollection") {
    return {
      type: "FeatureCollection",
      features: (Array.isArray(input.features) ? input.features : [])
        .map(normalizeFeature)
        .filter(Boolean),
    };
  }

  if (input.type === "Feature") {
    const feature = normalizeFeature(input);
    return {
      type: "FeatureCollection",
      features: feature ? [feature] : [],
    };
  }

  if (["Polygon", "MultiPolygon"].includes(input.type)) {
    return {
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          properties: {},
          geometry: input,
        },
      ],
    };
  }

  return { type: "FeatureCollection", features: [] };
}

function normalizeFeature(feature) {
  if (!feature || typeof feature !== "object") return null;
  if (!feature.geometry || typeof feature.geometry !== "object") return null;
  if (!["Polygon", "MultiPolygon"].includes(feature.geometry.type)) return null;
  return {
    type: "Feature",
    properties: feature.properties && typeof feature.properties === "object" ? feature.properties : {},
    geometry: feature.geometry,
  };
}

function getCollectionBounds(features = []) {
  const initial = {
    minX: Number.POSITIVE_INFINITY,
    minY: Number.POSITIVE_INFINITY,
    maxX: Number.NEGATIVE_INFINITY,
    maxY: Number.NEGATIVE_INFINITY,
  };

  const bounds = features.reduce((acc, feature) => mergeBounds(acc, getGeometryBounds(feature.geometry)), initial);
  if (!Number.isFinite(bounds.minX)) {
    return {
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0,
    };
  }
  return bounds;
}

function getGeometryBounds(geometry) {
  const points = [];
  visitGeometryPoints(geometry, (point) => {
    points.push(point);
  });

  if (!points.length) {
    return {
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0,
    };
  }

  return points.reduce(
    (acc, [x, y]) => ({
      minX: Math.min(acc.minX, x),
      minY: Math.min(acc.minY, y),
      maxX: Math.max(acc.maxX, x),
      maxY: Math.max(acc.maxY, y),
    }),
    {
      minX: Number.POSITIVE_INFINITY,
      minY: Number.POSITIVE_INFINITY,
      maxX: Number.NEGATIVE_INFINITY,
      maxY: Number.NEGATIVE_INFINITY,
    }
  );
}

function mergeBounds(left, right) {
  return {
    minX: Math.min(left.minX, right.minX),
    minY: Math.min(left.minY, right.minY),
    maxX: Math.max(left.maxX, right.maxX),
    maxY: Math.max(left.maxY, right.maxY),
  };
}

function mapGeometryPoints(geometry, mapper) {
  if (!geometry || typeof geometry !== "object") return null;
  if (geometry.type === "Polygon") {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map((ring) => ring.map((point) => mapper(point))),
    };
  }
  if (geometry.type === "MultiPolygon") {
    return {
      ...geometry,
      coordinates: geometry.coordinates.map((polygon) =>
        polygon.map((ring) => ring.map((point) => mapper(point)))
      ),
    };
  }
  return geometry;
}

function visitGeometryPoints(geometry, visitor) {
  if (!geometry || typeof geometry !== "object") return;
  if (geometry.type === "Polygon") {
    for (const ring of geometry.coordinates || []) {
      for (const point of ring || []) {
        if (Array.isArray(point) && point.length >= 2) {
          visitor([Number(point[0]) || 0, Number(point[1]) || 0]);
        }
      }
    }
    return;
  }

  if (geometry.type === "MultiPolygon") {
    for (const polygon of geometry.coordinates || []) {
      for (const ring of polygon || []) {
        for (const point of ring || []) {
          if (Array.isArray(point) && point.length >= 2) {
            visitor([Number(point[0]) || 0, Number(point[1]) || 0]);
          }
        }
      }
    }
  }
}
